import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MAIN_PRODUCT_GROUPS } from "@/lib/mainProductIds";
import type { User, Session } from "@supabase/supabase-js";
import { useAppSettings } from "@/hooks/useAppSettings";
import i18n from "@/i18n";

export type AccessMode = "owned" | "subscription" | "blocked";

interface UserSubscription {
  id: string;
  subscription_offer_id: string;
  subscription_status: string;
  expires_at: string | null;
}

interface AppUser {
  id: string;
  auth_id: string;
  email: string;
  isAdmin: boolean;
  products: string[]; // product_ids from user_products (permanent access)
  subscriptions: UserSubscription[];
  avatar_url: string | null;
  display_name: string | null;
  /** Language the user explicitly chose in Settings, persisted across devices */
  preferred_language: string | null;
}

interface AuthContextType {
  user: AppUser | null;
  session: Session | null;
  /**
   * `error` is already translated in the app's current language. `errorKey` is
   * the raw i18n key alongside it, so a screen pinned to a fixed language (a
   * single-market product namespace) can re-translate instead of showing the
   * message in the user's global language.
   */
  login: (email: string) => Promise<{ error?: string; errorKey?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasProduct: (productId: string) => boolean;
  hasPermanentAccess: (productId: string) => boolean;
  /** Omit productId to check for ANY active subscription (any offer). Pass a
   *  specific Hotmart product id to scope the check to that offer only —
   *  required wherever a subscription unlocks something tied to ONE
   *  product, so an active subscription to a different offer doesn't also
   *  read as active for this one. */
  hasActiveSubscription: (productId?: string) => boolean;
  getAccessMode: (productId: string) => AccessMode;
  testerMode: boolean;
  testerProducts: string[];
  testerSubscriptionStatus: string | null; // null = no sub, "active" | "canceled" | "expired" | "past_due" | "inactive"
  setTesterMode: (active: boolean) => void;
  setTesterProducts: (products: string[]) => void;
  setTesterSubscriptionStatus: (status: string | null) => void;
  refreshProfile: () => Promise<void>;
  /** In tester mode returns testerProducts, otherwise user.products */
  effectiveProducts: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_CONTEXT_FALLBACK: AuthContextType = {
  user: null,
  session: null,
  login: async () => ({ error: i18n.t("auth_errors.connection_error"), errorKey: "auth_errors.connection_error" }),
  logout: () => {},
  isAuthenticated: false,
  loading: false,
  hasProduct: () => true,
  hasPermanentAccess: () => false,
  hasActiveSubscription: () => false,
  getAccessMode: () => "blocked",
  testerMode: false,
  testerProducts: [],
  testerSubscriptionStatus: null,
  setTesterMode: () => {},
  setTesterProducts: () => {},
  setTesterSubscriptionStatus: () => {},
  refreshProfile: async () => {},
  effectiveProducts: [],
};

/** Check if a subscription is currently active.
 *
 * Grace period: when Hotmart marks a charge as delayed/past_due it typically
 * retries within 1-3 days. We keep the subscription active for up to 5 days
 * after the last known expiry date so users don't lose access mid-retry cycle.
 */
const PAST_DUE_GRACE_DAYS = 5;

/**
 * Days a time-limited user_products row survives past its paid-through date.
 *
 * Hotmart bills Glucofy AI monthly as PURCHASE_APPROVED and reports a failed
 * charge as PURCHASE_DELAYED, retrying for a while before giving up. Cutting
 * access the moment a charge slips would punish a card that expired over a
 * weekend, so a row stays valid for one extra cycle after the date it was paid
 * through. Beyond that the person has had a full unpaid month.
 *
 * Rows with expires_at null are untouched by any of this: that is the 57,710
 * one-off purchases, and they never expire.
 */
const PAID_THROUGH_GRACE_DAYS = 30;

/** Whether a user_products row still grants access. */
export function isProductRowActive(
  row: { expires_at?: string | null },
  now: Date = new Date(),
): boolean {
  if (!row.expires_at) return true; // bought outright — forever
  const deadline = new Date(row.expires_at);
  if (isNaN(deadline.getTime())) return true; // unparseable date must not lock anyone out
  deadline.setDate(deadline.getDate() + PAID_THROUGH_GRACE_DAYS);
  return deadline > now;
}

function isSubActive(sub: UserSubscription): boolean {
  if (sub.subscription_status === "active") {
    if (!sub.expires_at) return true;
    return new Date(sub.expires_at) > new Date();
  }

  // A canceled subscription keeps access through the period the user
  // already paid for — canceling must not claw back time they paid for.
  if (sub.subscription_status === "canceled") {
    if (!sub.expires_at) return false; // nothing paid-through to honor
    return new Date(sub.expires_at) > new Date();
  }

  // Grant a grace period for delayed/past_due subscriptions
  if (sub.subscription_status === "past_due") {
    if (!sub.expires_at) return true; // no expiry recorded — keep active
    const graceDeadline = new Date(sub.expires_at);
    graceDeadline.setDate(graceDeadline.getDate() + PAST_DUE_GRACE_DAYS);
    return graceDeadline > new Date();
  }

  return false;
}

/**
 * Check if a user owns a product, considering variant IDs.
 * E.g. if module requires "7339562" but user owns "7368977",
 * both are Cocina Real variants → returns true.
 */
function ownsProductOrVariant(ownedIds: string[], requiredId: string): boolean {
  if (ownedIds.includes(requiredId)) return true;
  // Check if requiredId belongs to a product group where user owns any variant
  for (const group of MAIN_PRODUCT_GROUPS) {
    if (group.ids.includes(requiredId)) {
      return group.ids.some((vid) => ownedIds.includes(vid));
    }
  }
  return false;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [testerMode, setTesterMode] = useState(false);
  const [testerProducts, setTesterProducts] = useState<string[]>([]);
  const [testerSubscriptionStatus, setTesterSubscriptionStatus] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Global access override from app_settings (realtime-enabled via useAppSettings)
  const { data: appSettings } = useAppSettings();
  const isGlobalOverride = appSettings?.global_access_override === "true";

  const initialSessionResolvedRef = useRef(false);

  const fetchUserData = useCallback(async (authUser: User): Promise<AppUser | null> => {
    const normalizedEmail = authUser.email?.toLowerCase().trim() ?? null;

    try {
      // Refresh session AND look up app_user by auth_id in parallel.
      // getSession() must run first to avoid concurrent token refresh races,
      // but the app_users query can share the same refreshed token safely.
      const [, authLinkedResult] = await Promise.all([
        supabase.auth.getSession(),
        supabase
          .from("app_users")
          .select("id, email, avatar_url, display_name, auth_id, preferred_language")
          .eq("auth_id", authUser.id)
          .limit(1)
          .maybeSingle(),
      ]);

      const { data: authLinkedUser, error: authLinkedUserError } = authLinkedResult;
      if (authLinkedUserError) throw authLinkedUserError;

      let appUser: {
        id: string;
        email: string;
        avatar_url: string | null;
        display_name: string | null;
        auth_id: string | null;
        preferred_language: string | null;
      } | null = authLinkedUser;

      if (!appUser && normalizedEmail) {
        const { data: emailMatchedUser, error: emailMatchedUserError } = await supabase
          .from("app_users")
          .select("id, email, avatar_url, display_name, auth_id, preferred_language")
          .eq("email", normalizedEmail)
          .limit(1)
          .maybeSingle();

        if (emailMatchedUserError) {
          throw emailMatchedUserError;
        }

        appUser = emailMatchedUser;

        if (appUser && appUser.auth_id !== authUser.id) {
          const { error: relinkError } = await supabase
            .from("app_users")
            .update({ auth_id: authUser.id })
            .eq("id", appUser.id);

          if (relinkError) {
            console.error("Auth profile relink error:", relinkError);
          } else {
            appUser = { ...appUser, auth_id: authUser.id };
          }
        }
      }

      if (!appUser) {
        return null;
      }

      const [rolesResult, productsResult, subsResult] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", appUser.id),
        supabase.from("user_products").select("product_id, expires_at").eq("user_id", appUser.id),
        supabase.from("user_subscriptions").select("id, subscription_offer_id, subscription_status, expires_at").eq("user_id", appUser.id),
      ]);

      if (rolesResult.error) throw rolesResult.error;
      if (productsResult.error) throw productsResult.error;
      if (subsResult.error) throw subsResult.error;

      return {
        id: appUser.id,
        auth_id: appUser.auth_id ?? authUser.id,
        email: appUser.email,
        isAdmin: rolesResult.data?.some((r: any) => r.role === "admin") ?? false,
        // A row that has run past its paid-through date (plus grace) no longer
        // counts as owned — without this filter, expires_at would be written
        // by the webhook and then ignored by everything that reads access.
        products: (productsResult.data as any[] ?? [])
          .filter((p) => isProductRowActive(p))
          .map((p) => p.product_id),
        subscriptions: (subsResult.data as any[] ?? []).map((s) => ({
          id: s.id,
          subscription_offer_id: s.subscription_offer_id,
          subscription_status: s.subscription_status,
          expires_at: s.expires_at,
        })),
        avatar_url: appUser.avatar_url ?? null,
        display_name: appUser.display_name ?? null,
        preferred_language: appUser.preferred_language ?? null,
      };
    } catch (err) {
      console.error("Auth profile load error:", err);
      return null;
    }
  }, []);

  // Periodic refresh of user access data.
  // Users with no products yet: refresh every 30s (just bought, waiting for webhook).
  // Users with products: refresh every 5 minutes (normal polling).
  useEffect(() => {
    if (!user?.id) return;

    const refresh = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.user) return;
      const updated = await fetchUserData(currentSession.user);
      if (updated) setUser(updated);
    };

    const intervalMs = 5 * 60 * 1000;
    const interval = setInterval(refresh, intervalMs);
    return () => clearInterval(interval);
  }, [user?.id, user?.products?.length, fetchUserData]);

  useEffect(() => {
    let isMounted = true;

    const applySession = async (nextSession: Session | null) => {
      if (!isMounted) return;
      setSession(nextSession);

      if (nextSession?.user) {
        // Fetch user profile and warm module cache in parallel.
        // Module cache must be ready BEFORE setUser() triggers home to mount,
        // so module names are available on first render with no flash.
        const lang = i18n.language || "es";
        const [nextUser] = await Promise.all([
          fetchUserData(nextSession.user),
          queryClient.prefetchQuery({
            queryKey: ["recipe-modules-home", lang],
            queryFn: async () => {
              const { data } = await supabase
                .from("recipe_modules")
                .select("*")
                .eq("active", true)
                .order("sort_order");
              return data || [];
            },
            staleTime: 1000 * 60 * 60, // 60 min — matches Inicio.tsx query
          }),
        ]);
        if (isMounted) {
          setUser(nextUser);
          // Language resolution is centralized in useAutoLocale (the single
          // resolver). AuthContext intentionally does NOT set the language here:
          // doing so raced with useAutoLocale and — because the product-based
          // path had no manual-selection guard — reset the user's chosen
          // language on every auth event (login, background resume, token
          // refresh). useAutoLocale honours the priority:
          // manual choice > DB preferred_language > product > device > es.
        }
        return;
      }

      setUser(null);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        void (async () => {
          try {
            // Only show the loading screen during the very first session resolution.
            // After that, token refreshes (SIGNED_IN fired when app returns from background,
            // TOKEN_REFRESHED, USER_UPDATED) must NOT unmount the current page — that resets
            // all in-progress state (photos, forms, camera flow, etc.).
            if ((event === "SIGNED_IN" || event === "USER_UPDATED") && isMounted && !initialSessionResolvedRef.current) {
              setLoading(true);
            }

            await applySession(nextSession);
          } catch (err) {
            // If applySession ever throws (unexpectedly — fetchUserData already
            // catches its own errors), don't leave the app stuck on the loading
            // screen forever. This is what "app freezes/closes on login" looks
            // like from the outside: loading never flips back to false.
            console.error("[AuthContext] onAuthStateChange handler failed:", err);
          } finally {
            if (initialSessionResolvedRef.current && isMounted) {
              setLoading(false);
            }
          }
        })();
      }
    );

    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await applySession(session);
      } catch (err) {
        console.error("[AuthContext] initial session resolution failed:", err);
      } finally {
        initialSessionResolvedRef.current = true;
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserData]);

  const login = async (email: string): Promise<{ error?: string; errorKey?: string }> => {
    const invokeOnce = () =>
      supabase.functions.invoke("auth-login", {
        body: { email: email.toLowerCase().trim() },
      });

    let res: Awaited<ReturnType<typeof invokeOnce>>;

    try {
      res = await invokeOnce();
    } catch {
      try {
        res = await invokeOnce();
      } catch {
        return { error: i18n.t("auth_errors.connection_error"), errorKey: "auth_errors.connection_error" };
      }
    }

    if (res.error) {
      let code = "";
      try {
        const body = typeof res.error === "object" && "context" in res.error
          ? await (res.error as any).context?.json?.()
          : null;
        code = body?.code || "";
      } catch { /* ignore */ }

      if (!code) code = res.data?.code || "";

      if (code === "USER_NOT_REGISTERED") {
        return { error: i18n.t("auth_errors.email_not_registered"), errorKey: "auth_errors.email_not_registered" };
      }
      if (code === "PROVISIONING_IN_PROGRESS") {
        return { error: i18n.t("auth_errors.access_provisioning"), errorKey: "auth_errors.access_provisioning" };
      }
      if (code === "AUTH_LINK_FAILED" || code === "AUTH_CREATE_FAILED") {
        return { error: i18n.t("auth_errors.session_error"), errorKey: "auth_errors.session_error" };
      }
      if (code === "CONFIG_ERROR" || code === "SERVER_ERROR") {
        return { error: i18n.t("auth_errors.server_error"), errorKey: "auth_errors.server_error" };
      }
      return { error: i18n.t("auth_errors.login_error"), errorKey: "auth_errors.login_error" };
    }

    const data = res.data;
    if (data?.code && data.code !== "OK") {
      if (data.code === "USER_NOT_REGISTERED") {
        return { error: i18n.t("auth_errors.email_not_registered"), errorKey: "auth_errors.email_not_registered" };
      }
      if (data.code === "PROVISIONING_IN_PROGRESS") {
        return { error: i18n.t("auth_errors.access_provisioning"), errorKey: "auth_errors.access_provisioning" };
      }
      return { error: data.error || i18n.t("auth_errors.login_error"), errorKey: "auth_errors.login_error" };
    }

    const { error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: data.token_hash,
      type: "magiclink",
    });

    if (verifyError) {
      console.error("OTP verify error:", verifyError);
      return { error: i18n.t("auth_errors.verify_error"), errorKey: "auth_errors.verify_error" };
    }

    return {};
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const refreshProfile = async () => {
    // Full refresh: reload all access data (products + subscriptions + profile)
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession?.user) return;
    const updated = await fetchUserData(currentSession.user);
    if (updated) setUser(updated);
  };


  /** Effective products list (respects tester mode) */
  const effectiveProducts = useMemo(() => {
    if (user?.isAdmin && testerMode) return testerProducts;
    return user?.products ?? [];
  }, [user, testerMode, testerProducts]);

  /** Check if user owns a product permanently (with variant resolution) */
  const hasPermanentAccess = useCallback((productId: string): boolean => {
    if (!user) return false;
    // Tester mode takes absolute priority — even over global override
    if (user.isAdmin && testerMode) return ownsProductOrVariant(testerProducts, productId);
    if (user.isAdmin) return true;
    if (isGlobalOverride) return true;
    return ownsProductOrVariant(user.products, productId);
  }, [user, testerMode, testerProducts, isGlobalOverride]);

  /** Check if user has an active subscription — any offer if productId is
   *  omitted, or a specific offer if given (see the interface doc comment).
   *  NOTE: Global override does NOT grant subscriber status — it only unlocks content.
   *  This ensures freemium limits (Natasha, GlucoPhoto) still apply to global-override users. */
  const hasActiveSubscription = useCallback((productId?: string): boolean => {
    if (!user) return false;
    if (user.isAdmin && testerMode) return testerSubscriptionStatus === "active";
    return user.subscriptions.some(
      (s) => (!productId || s.subscription_offer_id === productId) && isSubActive(s)
    );
  }, [user, testerMode, testerSubscriptionStatus]);

  /** Get the access mode for a specific product (with variant resolution)
   *
   *  Priority (highest → lowest):
   *  1. testerMode (admin only) — simulates any scenario, ignores all overrides
   *  2. admin (non-tester) — always "owned"
   *  3. global_access_override — emergency operational unlock
   *  4. permanent product ownership
   *  5. active subscription
   *  6. blocked
   */
  const getAccessMode = useCallback((productId: string): AccessMode => {
    if (!user) return "blocked";

    // 1. Tester mode — absolute priority, never bypassed
    if (user.isAdmin && testerMode) {
      if (ownsProductOrVariant(testerProducts, productId)) return "owned";
      if (testerSubscriptionStatus === "active") return "subscription";
      return "blocked";
    }

    // 2. Admin (non-tester) — full access
    if (user.isAdmin) return "owned";

    // 3. Global emergency override
    if (isGlobalOverride) return "owned";

    // 4. Permanent product ownership
    if (ownsProductOrVariant(user.products, productId) || user.products.includes("all")) {
      return "owned";
    }

    // 5. Active subscription for THIS product specifically — an active
    // subscription to one offer must not unlock a different
    // subscription-gated product it wasn't paid for.
    if (user.subscriptions.some((s) => s.subscription_offer_id === productId && isSubActive(s))) {
      return "subscription";
    }

    // 6. Blocked
    return "blocked";
  }, [user, testerMode, testerProducts, testerSubscriptionStatus, isGlobalOverride]);

  const hasProduct = useCallback((productId: string): boolean => {
    return hasPermanentAccess(productId);
  }, [hasPermanentAccess]);

  return (
    <AuthContext.Provider value={{
      user, session, login, logout,
      isAuthenticated: !!session, loading,
      hasProduct, hasPermanentAccess, hasActiveSubscription, getAccessMode,
      testerMode, testerProducts, testerSubscriptionStatus,
      setTesterMode, setTesterProducts, setTesterSubscriptionStatus,
      refreshProfile, effectiveProducts,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    console.error("AuthContext missing: using safe fallback.");
    return AUTH_CONTEXT_FALLBACK;
  }
  return ctx;
};
